import { useQuery, useMutation } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { z } from "zod";
import { zodParseWithLog } from "src/api/apiErrorLogs.ts";
import { useSnackbar } from "src/utils/useSnackbar";
import { useTranslation } from "react-i18next";

const commentSchema = z.object({
    id: z.string(),
    comment: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    createdBy: z.object({
        firstname: z.string(),
        lastname: z.string(),
    }),
});

const ssqPostValueSchema = z.object({
    ssqPropertyId: z.number(),
    ssqPostElementId: z.number(),
    value: z.string(),
    position: z.number(),
    comments: z.array(commentSchema).optional(),
});
type SsqPostValue = z.infer<typeof ssqPostValueSchema>;

export const getOneAttachmentKey = "getOneAttachment" as const;

type useSsqPostElementOptions = {
    postId: number;
    propertyId: number;
    position: number;
};

type saveCommentOptions = {
    comment: string;
    ssqPostElementId: number;
};

export const useSsqPostElement = (options: useSsqPostElementOptions) => {
    const { postId, propertyId, position } = options;
    const queryKey = ["useSsqPostElement", postId, propertyId] as const;
    return useQuery({
        queryKey: queryKey,
        queryFn: async () => {
            const url = `/api/ssq_posts/${postId}/values`;
            const response = await loggedInApiCall.get(url);
            return response;
        },
        select: (response) => {
            const dataArray = response.data.data;
            const parsed: SsqPostValue[] = zodParseWithLog(dataArray, ssqPostValueSchema.array(), "useSsqPostElement");
            const matching = parsed.find((item) => item.ssqPropertyId === propertyId && item.position === position);
            return matching ?? null;
        },
        onError: (error) => {
            console.error("Error when fetching ssqPostElementId:", error);
        },
    });
};

export const useSaveComment = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();
    return useMutation({
        mutationFn: async (options: saveCommentOptions) => {
            const { comment, ssqPostElementId } = options;
            await loggedInApiCall.post(`/api/diagnostic/ssq_post_comment`, {
                comment: comment,
                ssqPostElementId: ssqPostElementId,
            });
            return true;
        },
        onError: (error) => {
            console.error("Note saving step failed : ", error);
            snackbar.setSnackbarProps({
                type: "error",
                message: t("property.note.save_error"),
            });
        },
        onSettled: () => {},
    });
};

export const useDeleteComment = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();
    return useMutation({
        mutationFn: async (commentId: string) => {
            await loggedInApiCall.delete(`/api/diagnostic/ssq_post_comment/${commentId}`);
            return true;
        },
        onError: (error) => {
            console.error("Note deletion failed : ", error);
            snackbar.setSnackbarProps({
                type: "error",
                message: t("property.note.deletion_error"),
            });
        },
        onSettled: () => {},
    });
};
